import { useCallback, useEffect, useMemo, useState } from "react"

import { ProjectHistoryEntry } from "entities/projects/types/ProjectHistoryEntry"
import { ProjectHistoryFilterState } from "widgets/project-history/project-history-filter/ProjectHistoryFilter"

import { ProjectHistoryDisplayRow } from "../types/ProjectHistoryTable"
import { ProjectHistoryModel } from "./useProjectHistoryModel"

export interface ProjectHistoryViewModel {
  isLoading: boolean
  rows: ProjectHistoryDisplayRow[]
  hasMore: boolean
  loadMore: () => void
  getElementUrl: (entry: ProjectHistoryEntry) => string
}

export function useProjectHistoryViewModel(
  { entries, hasMore, loadMore, isLoading, getElementUrl }: ProjectHistoryModel,
  filterValues: ProjectHistoryFilterState
): ProjectHistoryViewModel {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    setExpandedIds(new Set())
  }, [filterValues])

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const rows = useMemo<ProjectHistoryDisplayRow[]>(
    () =>
      entries.reduce<ProjectHistoryDisplayRow[]>((result, entry) => {
        const isExpandable = (entry.changes?.length ?? 0) > 1
        const isExpanded = expandedIds.has(entry.id)

        result.push({
          kind: "entry",
          entry,
          isExpandable,
          isExpanded,
          onToggle: () => toggleExpanded(entry.id)
        })

        if (isExpanded) {
          entry.changes?.forEach((change, changeIndex) => {
            result.push({
              kind: "changeDetail",
              parentEntry: entry,
              changeIndex,
              field: change.field,
              oldValue: change.oldValue,
              newValue: change.newValue
            })
          })
        }

        return result
      }, []),
    [entries, expandedIds, toggleExpanded]
  )
  return {
    isLoading,
    rows,
    hasMore,
    loadMore,
    getElementUrl
  }
}
